import React, { useRef, useState } from "react";
import TableCell from "@material-ui/core/TableCell";
import cn from "classnames";
import IconButton from "../../icon-button";
import { FormattedMessage } from "react-intl";
import { TableIconCell, CustomTableRow } from "../../table";
import OptionalDate from "../../optional-date";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import withStyles from "../../with-styles";
import styles from "./styles.css.js";
import { eventStatusTypes } from "../../../pages/event";
import { downloadRecordingService, getLocalRecordingsService } from "../../../services/commonService";
import ApiConnectedPaginatedTable from "../../table/paginated-table/api-connected-paginated-table";
import DownloadIcon from "../../buttons/download-button/parts/download-icon";
import { STREAM_SOURCE_OPERATION_DOWNLOAD_RECORDING } from "../../../pages/local-records/local-records-constants";

const Header = () => {
  return <></>;
};

const StyledHeaderComponent = withStyles(styles)(Header);

const RowComponent = ({ item, classes, intl }) => {
  const linkDisabled = item.event && item.event.eventStatus && item.event.eventStatus === eventStatusTypes.STARTED;

  const [loadingDownload, setLoadingDownload] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const handleSetLoadingDownload = value => {
    setLoadingDownload(value);
    if (value === null) {
      setDownloadError(null);
    }
  };

  const handleDownloadError = error => {
    setLoadingDownload(null);
    setDownloadError(item.recording_id);
  };

  const handleDownload = () => {
    handleSetLoadingDownload(item.recording_id);
    setDownloadError(null);
    downloadRecordingService(
      {
        streamsourceId: item.streamsource_id,
        recording_id: item.recording_id,
        action: STREAM_SOURCE_OPERATION_DOWNLOAD_RECORDING,
      },
      handleSetLoadingDownload,
      handleDownloadError,
    );
  };

  return (
    <>
      <CustomTableRow
        key={item.id}
        disabled={linkDisabled}
        className={cn(classes.row, {
          [classes.main]: item.isMainEventRecording,
        })}
      >
        <TableCell width="20%">
          <div className={classes.title}>{item.recording_id}</div>
        </TableCell>
        <TableCell width="60%">
          <div className={classes.date_wrapper}>
            <div className={classes.date_container}>
              <OptionalDate
                label={intl.formatMessage({
                  id: "event.startDate",
                })}
                className={cn(classes.date, {
                  [classes.main]: item.event_id === "continuous",
                  [classes.date__finished]: item.event_id !== "continuous",
                })}
                icon="event-available"
                value={item.start}
              />
              <OptionalDate
                label={intl.formatMessage({
                  id: "event.endDate",
                })}
                className={cn(classes.date, {
                  [classes.main]: item.event_id === "continuous",
                  [classes.date__finished]: item.event_id !== "continuous",
                })}
                icon="event-available"
                value={item.end}
              />
            </div>
          </div>
        </TableCell>
        <TableIconCell width="20%">
          <div className={classes.rowButtonsContainer}>
            <div className={cn(classes.waitingText, { [classes.main]: item.event_id === "continuous" })}>
              {item.event_id === "continuous" && <FormattedMessage id="recording.download.waiting" />}
            </div>
            {loadingDownload === item.recording_id ? (
              <div className={classes.waitingText}>
                <FormattedMessage id="recording.download.loading" />
              </div>
            ) : downloadError === item.recording_id ? (
              <div className={classes.waitingText} style={{ color: "red" }}>
                <FormattedMessage id="recording.download.error" />
              </div>
            ) : (
              <IconButton
                onClick={() => handleDownload(item)}
                disabled={item.event_id === "continuous"}
                className={cn(classes.recording_download__main, {
                  [classes.recording_download__main__disabled]: item.event_id === "continuous",
                })}
              >
                <DownloadIcon
                  compactStyle={true}
                  disabled={item.event_id === "continuous"}
                  disabledClass={classes.recording_download__main__disabled}
                />
              </IconButton>
            )}
          </div>
        </TableIconCell>
      </CustomTableRow>
    </>
  );
};

RowComponent.propTypes = {
  /**
   ** Elemento a representar en la fila de la tabla
   **/
  item: PropTypes.object,
  /**
   ** Referencia con el último evento de la anterior grabación de la página
   **/
  previousEvent: PropTypes.object,
};

const StyledI18nRowComponent = withStyles(styles)(injectIntl(RowComponent));

const LocalRecordingsPaginatedTable = ({ page, pageName, classes, ...props }) => {
  const previousEvent = useRef();

  const renderHeaderComponent = title => {
    return <StyledHeaderComponent title={title} />;
  };

  return (
    <>
      <div className={classes.titleTableContent}>
        <FormattedMessage id="app.page.source.local.recordings.title" />
      </div>
      <ApiConnectedPaginatedTable
        APIPetition={getLocalRecordingsService}
        headerComponent={() => renderHeaderComponent(props.variables.eventTitle)}
        rowComponent={StyledI18nRowComponent}
        paginatedElementsHandler={data => data}
        page={page}
        pageName={pageName}
        rowProps={{ previousEvent }}
        noDataMessage={"local_recordings.no_data"}
        {...props}
      />
    </>
  );
};

LocalRecordingsPaginatedTable.propTypes = {
  /**
   ** Página a cargar en la tabla
   **/
  page: PropTypes.number,
  /**
   ** Objeto que contiene los filtrados de la búsqueda
   **/
  variables: PropTypes.object,
  /**
   ** Componente que se mostrará en el pie de la tabla
   **/
  footer: PropTypes.element,
};

export default withStyles(styles)(LocalRecordingsPaginatedTable);
